import React, { useMemo } from 'react';

import { LocalTime } from '@js-joda/core';
import { Box, Divider, Flex, Image, Text } from '@opendoor/bricks/core';

import { LegacyExternalLink } from 'platform/remix-run/bricks';

import { TIME_ONLY_FORMAT } from 'shared/helpers/datetime';
import { formatCurrency } from 'shared/helpers/formatters';
import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { EmptyImage } from 'static/images/EmptyImage';

import {
  BuyDirectCheckoutFlowStateMachineState,
  CheckoutState,
} from 'buying/stateMachines/buyDirectCheckoutFlowStateMachine';

interface IInfoPanelProps {
  bathrooms?: number;
  bedrooms?: number;
  buyDirectDiscount: number;
  city?: string;
  currentState: BuyDirectCheckoutFlowStateMachineState['value'];
  listPrice: number;
  photo?: string;
  postalCode?: string;
  previousState?: CheckoutState;
  sellerDisclosuresDownloadUrl?: string;
  state?: string;
  street?: string;
  sqft?: number | null;
}

/**
 * Left info panel for the Buy Direct checkout flow
 * Tightly coupled to the state machine nodes
 * storybook-check-ignore
 */
export const InfoPanel: React.FC<IInfoPanelProps> = React.memo(
  ({
    buyDirectDiscount,
    bathrooms,
    bedrooms,
    city,
    currentState,
    listPrice,
    photo,
    postalCode,
    previousState,
    sellerDisclosuresDownloadUrl,
    state,
    street,
    sqft,
  }) => {
    const { isMobile } = useMobileDevice();

    let headlineCopy = '';
    let subcopy = '';

    const photoToDisplay = useMemo(() => {
      if (photo) {
        return (
          <Image
            alt="Property"
            height={['120px', '73%', '200px', '140px']}
            width={['120px', '40%', '300px', '163px']}
            src={photo}
            objectFit="cover"
            borderRadius="semiRounded"
            mx={[0, 0, 0, 4]}
          />
        );
      }
      return (
        <Box>
          <EmptyImage
            height={isMobile ? '120px' : '108px'}
            width={isMobile ? '120px' : '163px'}
            style={{
              objectFit: 'cover',
            }}
          ></EmptyImage>
        </Box>
      );
    }, [isMobile, photo]);

    switch (currentState) {
      case CheckoutState.Idle:
      case CheckoutState.ReviewDetails:
        headlineCopy = 'Buy directly from Opendoor';
        break;
      case CheckoutState.ContactDetails:
        headlineCopy = 'Provide a few details';
        break;
      case CheckoutState.FundingSource:
        headlineCopy = 'How you plan to buy';
        break;
      case CheckoutState.PreApproval:
      case CheckoutState.PreApprovalConfirmation:
        headlineCopy = 'Share your pre-approval letter';
        break;
      case CheckoutState.ReviewContractDetails:
        headlineCopy = 'Review details for your contract';
        break;
      case CheckoutState.OwnAgent:
        headlineCopy = 'Since you’re working with an agent';
        break;
      case CheckoutState.ContingentSale:
        headlineCopy = 'Since you need to sell your current house';
        break;
      case CheckoutState.BackupOfferDetails:
        headlineCopy = 'You may still be able to buy this home';
        subcopy = `We accepted an offer for this home. But a benefit of buying from Opendoor is that buyers can back out without penalty. So you may still be able to get the home at our lower price. Share your details, and we’ll reach out if it becomes available.`;
        break;
      case CheckoutState.OfferDeadline:
        headlineCopy = 'Let’s get some details for your offer';
        subcopy =
          'Share your contact details and we’ll reach out to guide you through how to make an offer.';
        break;
      case CheckoutState.InContract:
        headlineCopy = 'Share your details';
        break;
      case CheckoutState.Confirmation:
        if (previousState === CheckoutState.CreateBuyDirectBackupOffer) {
          headlineCopy = 'We got your details';
          subcopy =
            'We’ll reach out if the buyer changes their mind and the home becomes available.';
        }
        if (previousState === CheckoutState.CreateBuyDirectListingOfferDeadline) {
          const ninetyMinutesFromNow = LocalTime.now().plusMinutes(90);
          const formattedTime = ninetyMinutesFromNow.format(TIME_ONLY_FORMAT);
          headlineCopy = `We got your details. We’ll call you by ${formattedTime}`;
        }
        break;
      default:
        break;
    }
    return (
      <Flex flexDirection="column" mx={[8, 8, 0, 0]} my={[-6, -6, 0, 0]}>
        <Box px={[6, 0, 0, 8]}>
          <Text size={isMobile ? '180' : '180'} fontWeight="medium">
            {headlineCopy}
          </Text>
          {subcopy && (
            <Text size={isMobile ? '100' : '110'} fontWeight="regular" mt={4}>
              {subcopy}
            </Text>
          )}
        </Box>
        <Flex
          flexDirection={['row', 'row', 'column', 'row']}
          pb={[6, 0, 0, 8]}
          pt={[4, 4, 6, 8]}
          px={[0, 0, 0, 6]}
          alignItems={['center', 'center', 'flex-start', 'center']}
        >
          {photoToDisplay}
          <Box px={[0, 4, 0, 4]} py={[0, 7, 7, 0]}>
            <Flex flexDirection="column">
              <Text size={isMobile ? '100' : '110'} fontWeight="medium" mb={2}>
                {formatCurrency(listPrice * buyDirectDiscount)} - buy direct price
              </Text>
              <Text size="100" fontWeight="regular" my={2}>
                {bedrooms} bds • {bathrooms} ba • {sqft} sqft{' '}
              </Text>
              <Text size="100" fontWeight="regular">
                {street}, {city}
              </Text>
              <Text size="100" fontWeight="regular">
                {state} {postalCode}
              </Text>
              {{ sellerDisclosuresDownloadUrl } && (
                <Text
                  size={isMobile ? '90' : '100'}
                  fontWeight="bold"
                  mt={3}
                  color="neutrals100"
                  textDecoration="underline"
                >
                  <LegacyExternalLink
                    href={sellerDisclosuresDownloadUrl}
                    analyticsName="seller_disclosure_notice"
                    aria-label="Seller's Disclosure Notice"
                    target="_blank"
                    rel="noreferrer"
                    color="neutrals100"
                  >
                    Seller's Disclosure Notice
                  </LegacyExternalLink>
                </Text>
              )}
            </Flex>
          </Box>
        </Flex>
        <Divider
          display={['block', 'block', 'none', 'none']}
          width="100%"
          alignSelf="center"
          mb={[8, 8, 0, 0]}
        />
      </Flex>
    );
  },
);

InfoPanel.displayName = 'InfoPanel';
